import { useState } from 'react';
import { Upload, type UploadedFile } from '@checkatrade/web-ui';
import { CameraFill } from '@cat-home-experts/mortar-iconography-web';
import { uploadImages as _uploadImages } from 'lib/media/api';

import Title from '../components/Title';
import Footer from '../components/Footer';

import { type ScreenProps } from '../types';

const UploadPictures = ({
  questionId,
  title,
  description,
  format,
  required,
  onNext,
  onSkip,
  savedAnswer,
  editMode,
}: ScreenProps) => {
  const [images, setImages] = useState<UploadedFile[]>(
    (savedAnswer?.answers as UploadedFile[]) || [],
  );

  const disableNext = images.length > 0 && required;

  const handleNext = () => {
    setImages([]);
    onNext({
      questionId,
      title,
      format,
      answers: images.map(i => ({ ...i, value: i.name, pending: false })),
    });
  };

  const handleSkip = () => {
    setImages([]);
    onSkip();
  };

  async function uploadImages(imagesToUpload: UploadedFile[]) {
    const formData = new FormData();

    imagesToUpload.forEach(image => {
      formData.append('images', image.file);
    });
    formData.append('source', 'REVIEW');

    try {
      const res = await _uploadImages({ formData: formData });

      const uploadedImagesRes = res.success;
      const uploadedImagesName = uploadedImagesRes.map(i => i.filename);

      const newImages = imagesToUpload
        .filter(img => uploadedImagesName.includes(img.name))
        .map(img => {
          const uploadedFile = uploadedImagesRes.find(
            _img => _img.filename === img.name,
          );
          return { ...img, id: uploadedFile?.id };
        });

      setImages(prev => [...prev, ...newImages]);

      return uploadedImagesRes.map(_img => ({
        id: _img.id,
        name: _img.filename,
      }));
    } catch (err) {
      return imagesToUpload.map(_img => ({
        id: _img.name,
        name: _img.name,
        error: 'Failed to upload image',
      }));
    }
  }

  async function removeImage(id: string | number) {
    setImages(prev => prev.filter(({ id: imageId }) => imageId !== id));
    return true;
  }

  return (
    <section className="flex h-screen flex-col justify-between overflow-auto">
      <div>
        <Title title={title} description={description} className="mb-4 mt-6" />
        <Upload
          name="images"
          onUpload={uploadImages}
          onRemove={removeImage}
          multiple
          maxFiles={5}
          files={images}
        >
          <div className="flex flex-col items-center justify-center gap-1">
            <CameraFill size={24} color="" className="fill-default-700" />
            Upload photos
          </div>
        </Upload>
      </div>
      <Footer
        disablePrimary={disableNext}
        showSecondary={!required || editMode}
        primaryLabel={editMode ? 'Done' : 'Next'}
        secondaryLabel={editMode ? 'Cancel' : 'Skip'}
        handlePrimaryButton={handleNext}
        handleSecondaryButton={handleSkip}
      />
    </section>
  );
};

export default UploadPictures;
